import { useContext, useMemo } from 'react'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import { sortPackageOptions } from 'lib/offer/offerPageUtil'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { min } from 'lib/array/arrayUtils'
import { formatInlineList } from 'lib/format/formatList'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getAvailablePackagesByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'

interface Params {
  room?: OfferPageRoom
}

interface SuggestedPackageResult {
  option: App.OfferPackageOptionView
  message: string
}

/**
 * Old logic which is to be superseded backend logic used via `useSuggestedPackageOption`.
 * But we're A/B testing the new logic vs. this old logic.
 */
function useSuggestedPackageOptionFromTotalSales({ room }: Params = {}): SuggestedPackageResult | undefined {
  const offerPageState = useContext(OfferPageStateContext)
  const { rooms: allRooms } = offerPageState

  const packageOptionsAllRooms = useAppSelector(state => getDurationPackageOptions(offerPageState, state))
  const packageOptionsByRoom = useAppSelector(state => getAvailablePackagesByRoom(offerPageState, state))

  const packageOptions = room ? packageOptionsByRoom[room.id] : packageOptionsAllRooms

  // Recommend package based on overall highest sales.
  // Note: Booking count is only available at the room type level, so effectively this is the
  // cheapest option of the most popular room type.
  return useMemo(() => {
    const rooms = room ? [room] : allRooms
    const sortedPackageOptions = sortPackageOptions(packageOptions)

    const availablePackages = sortedPackageOptions.filter(option => option.capacityValid && option.hotelAvailable)
    if (availablePackages.length <= 1) {
      return undefined // It would be silly to highlight the most popular option if there was only one option...
    }
    const highestBookingCount = Math.max(...availablePackages.map(p => p.package.roomType.stats.booked))
    const mostPopularPackages = availablePackages.filter(p => p.package.roomType.stats.booked === highestBookingCount)
    const popularOption = min(mostPopularPackages, option => option.hotelPrice ?? Number.MAX_SAFE_INTEGER)

    if (popularOption) {
      const occupantsMessage = formatInlineList(formatOccupantsShort(rooms).split(', '), 'conjunction', { style: 'long' })
      return {
        option: popularOption,
        message: occupantsMessage ? `Popular for ${occupantsMessage}` : '',
      }
    }

    return undefined
  }, [packageOptions, room, allRooms])
}

export default useSuggestedPackageOptionFromTotalSales
